import * as React from 'react'
import * as SwitchPrimitives from '@radix-ui/react-switch'

import { cn } from '@/utils'

export interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  /** Renders a third, indeterminate visual — thumb centered between the two ends, track a
   *  neutral gray — for a multi-selection whose members disagree on this boolean. `checked` is
   *  still a plain boolean underneath (Radix has no true third state) and still drives what a
   *  click resolves to; `mixed` only overrides the rendering, never the value. Opt-in and off by
   *  default, so every other `Switch` consumer (Freeze Object, Lock Object, etc.) is unaffected. */
  mixed?: boolean
}

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, mixed = false, ...props }, ref) => (
    <SwitchPrimitives.Root
      data-slot="switch"
      data-mixed={mixed || undefined}
      className={cn(
        'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
        mixed &&
          'data-[state=checked]:bg-muted-foreground/50 data-[state=unchecked]:bg-muted-foreground/50',
        className
      )}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
          mixed && 'data-[state=checked]:translate-x-2 data-[state=unchecked]:translate-x-2'
        )}
      />
    </SwitchPrimitives.Root>
  )
)

Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
